import * as React from 'react';
import {emptyObject} from '@packages/utilities/fp/utils';
import {noEmpty, chainListeners} from '@packages/utilities/component';
import {styled, emotionStyled} from '@packages/utilities/styled';
import {omitChildren} from '@packages/utilities/props';

import {Cell, Row} from './Layout';
import * as UiMenu from './UiMenu';

import {CheckDefaultIcon, View} from '@packages/systems/spring';

import type {CSSProperties} from '@packages/utilities/css-properties-type';

const InlineFlexRow = emotionStyled('div')({
  display: 'flex',
  justifyContent: 'space-between',
});

/**
 * Helps visually align the checkmark with the text - counteracting the
 * margin from DivCell.
 *
 * 2023-09-22
 */

const AlignedCheckmark = emotionStyled(CheckDefaultIcon)({
  color: '$textPrimary',
  marginTop: '-6px',
  position: 'relative',
  top: 3,
});

const EmptyCheckmarkPlaceholder = () => <View width={16} />;

// @ts-expect-error - TS7006 - Parameter 'props' implicitly has an 'any' type.
const DivCell = (props) => (
  <Cell {...omitChildren(props)} align="center">
    {props.children}
  </Cell>
);

const MaybeOneLineDivCell = styled(DivCell)(emptyObject, {
  singleLine: {
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  },
});

const NoEmptyCell = noEmpty(DivCell);

const Label = ({
  // @ts-expect-error - TS7031 - Binding element 'selectable' implicitly has an 'any' type.
  selectable,
  // @ts-expect-error - TS7031 - Binding element 'selected' implicitly has an 'any' type.
  selected,
  excludeCheckmarks = false,
  // @ts-expect-error - TS7031 - Binding element 'children' implicitly has an 'any' type.
  children,
  // @ts-expect-error - TS7031 - Binding element 'indicator' implicitly has an 'any' type.
  indicator,
  // @ts-expect-error - TS7031 - Binding element 'open' implicitly has an 'any' type.
  open,
  // @ts-expect-error - TS7031 - Binding element 'singleLine' implicitly has an 'any' type.
  singleLine,
  // @ts-expect-error - TS7031 - Binding element 'header' implicitly has an 'any' type.
  header,
  style = {},
}) => {
  const rowStyles: any = header ? {lineHeight: '12px', ...style} : style;

  return (
    <Row align="center" compact style={rowStyles}>
      {selectable && open && !excludeCheckmarks && (
        <DivCell>
          {selected ? (
            /**
             * data-checked is used for Cypress.
             */
            <AlignedCheckmark data-checked="true" />
          ) : (
            <EmptyCheckmarkPlaceholder />
          )}
        </DivCell>
      )}
      <MaybeOneLineDivCell grow={1} compact singleLine={singleLine}>
        {children}
      </MaybeOneLineDivCell>
      <NoEmptyCell compact>{indicator}</NoEmptyCell>
    </Row>
  );
};

type ItemProps<T> = {
  style?: CSSProperties;
  hover?: boolean;
  disabled?: boolean;
  disabledHover?: boolean;
  onMouseEnter?: (e: React.MouseEvent<HTMLElement>) => void;
  onMouseLeave?: (e: React.MouseEvent<HTMLElement>) => void;
  onClick?: (e: React.MouseEvent<HTMLElement>) => undefined | Promise<any>;
  onMouseDown?: (e: React.MouseEvent<HTMLElement>) => void;
  value?: T;
  onMouseUp?: (e: React.MouseEvent<HTMLElement>) => void;
  selectable?: boolean;
  excludeCheckmarks?: boolean;
  selected?: boolean;
  children: React.ReactNode;
  indicator?: React.ReactNode;
  header?: boolean;
  noHover?: boolean;
  onSecondaryClick?: (value?: T) => (e: React.MouseEvent<HTMLElement>) => void;
  SecondaryOption?: {
    props: {
      onClick: (e: React.MouseEvent<HTMLElement>) => void;
    };
  };
  open?: boolean;
  hasExternalLink?: boolean;
  ['data-automation-id']?: string;
  singleLine?: boolean;
  keepOutline?: boolean;
};

/**
 * @deprecated use `MenuItem` from `@packages/systems/spring` instead
 */
// @ts-expect-error - TS2322 - Type 'MemoExoticComponent<ForwardRefExoticComponent<...>>' is not assignable to type '<T>(arg1: ItemProps<T>) => ReactElement<any, string | JSXElementConstructor<any>>'.
export const Item: <T>(arg1: ItemProps<T>) => React.ReactElement = React.memo(
  React.forwardRef<HTMLElement, ItemProps<unknown>>(
    (
      {
        style,
        hover,
        disabled,
        disabledHover,
        onMouseEnter,
        onMouseLeave,
        onClick,
        onMouseDown,
        value,
        onMouseUp,
        selectable,
        excludeCheckmarks,
        selected,
        children,
        indicator,
        header,
        noHover,
        onSecondaryClick,
        SecondaryOption,
        open,
        hasExternalLink,
        'data-automation-id': dataAutomationId,
        singleLine,
        keepOutline,
        ...restProps
      },
      ref
    ) => {
      return (
        <UiMenu.Item
          ref={ref}
          data-legacy-ds="MenuItem"
          {...restProps}
          role="option"
          style={style}
          hover={hover}
          disabled={disabled}
          disabledHover={disabledHover}
          header={header}
          noHover={noHover}
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}
          onClick={onClick}
          onMouseDown={onMouseDown}
          onMouseUp={onMouseUp}
          cursor={hasExternalLink ? 'pointer' : 'default'}
          data-automation-id={dataAutomationId}
          keepOutline={keepOutline}
          selectable={selectable}
        >
          {SecondaryOption && hover ? (
            <InlineFlexRow>
              <div>
                <Label
                  selectable={selectable}
                  indicator={indicator}
                  selected={selected}
                  open={open}
                  excludeCheckmarks={excludeCheckmarks}
                  singleLine={singleLine}
                  header={header}
                >
                  {children}
                </Label>
              </div>
              {onSecondaryClick && SecondaryOption
                ? chainListeners(
                    {onClick: onSecondaryClick(value)},
                    SecondaryOption
                  )
                : null}
            </InlineFlexRow>
          ) : (
            <Label
              selectable={selectable}
              indicator={indicator}
              selected={selected}
              open={open}
              excludeCheckmarks={excludeCheckmarks}
              singleLine={singleLine}
              header={header}
            >
              {children}
            </Label>
          )}
        </UiMenu.Item>
      );
    }
  )
);

/**
 * @deprecated use `Menu` from `@packages/systems/spring` instead
 */
export const Wrapper = UiMenu.Wrapper;

/**
 * @deprecated use `MenuDivider` from `@packages/systems/spring` instead
 */
export const Divider = UiMenu.Divider;

/**
 * @deprecated use `MenuDivider` from `@packages/systems/spring` instead
 */
export const GroupDivider = UiMenu.GroupDivider;

/**
 * @deprecated use `MenuContent` from `@packages/systems/spring` instead
 */
export const Panel = UiMenu.Panel;
